import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateDielec } from "@/api/hooks/useDielce";
import { useHromadneZostavy } from "@/api/hooks/useHromadneZostavy";
import { useNastaveniaGlobal } from "@/api/hooks/useNastaveniaGlobal";
import { extractErrorMessage } from "@/api/client";
import { toast } from "@/components/ui/use-toast";
import { suggestNextKod } from "@/lib/dielecKod";
import { formatEur, parseDecimal } from "@/lib/format";
import { MJ_VLASTNEJ, predajVlastnej, type MjVlastnej } from "@/lib/vlastnaPolozka";
import type { Dielec, DielecCreate } from "@/api/schemas";

const cislo = (min: number, max: number, sprava: string) =>
  z
    .string()
    .trim()
    .min(1, sprava)
    .refine((v) => {
      const n = Number(parseDecimal(v));
      return Number.isFinite(n) && n >= min && n <= max;
    }, sprava);

const schema = z.object({
  nazov: z.string().trim().min(1, "Zadaj názov").max(100),
  dodavatel: z.string().trim().max(255),
  mj: z.enum(MJ_VLASTNEJ),
  // Desatinné (KOLO 211) — 12,5 m LED pásu; na tisíciny ako server
  mnozstvo: cislo(0.001, 1_000_000, "Zadaj množstvo (viac ako 0)").refine(
    (v) => /^\d+([.,]\d{1,3})?$/.test(v.trim()),
    "Najviac 3 desatinné miesta",
  ),
  nakup: cislo(0, 10_000_000, "Zadaj nákupnú cenu (0 a viac)"),
  rezia: cislo(0, 500, "Réžia 0 – 500 %"),
  // Práca €/MJ (KOLO 216) — nepovinná, réžia sa na ňu nevzťahuje
  praca: z
    .string()
    .trim()
    .refine((v) => {
      if (v === "") return true;
      const n = Number(parseDecimal(v));
      return Number.isFinite(n) && n >= 0 && n <= 999_999;
    }, "Práca 0 a viac"),
});
type FormData = z.infer<typeof schema>;

/**
 * Vlastná položka mimo cenníka (KOLO 210) — LED, oceľová konštrukcia od
 * zámočníka. User: *„nemusia byť v cenníku, stačí ich pridať, nastaviť cenu
 * a ukáže sa v konečnej spotrebe"*. Cena patrí riadku: nákup za MJ s réžiou
 * a práca za MJ (KOLO 216 — *„daj cenu práce, nie len prirážku"*), zisk
 * zostavy sa pripočíta ako pri každom riadku. Do cenníka nič nepribudne.
 *
 * Pri viacerých vybraných zostavách ide ten istý riadok do každej, jedným
 * krokom späť (ako kovanie a cenovka).
 */
export function VlastnaPolozkaDialog({
  zakazkaId,
  zostavaId,
  zostavaKod,
  existingDielce,
  dalsieZostavy = [],
  open,
  onOpenChange,
}: {
  zakazkaId: string;
  zostavaId: string;
  zostavaKod: string;
  existingDielce: Dielec[];
  dalsieZostavy?: { id: string; kod: string; existingDielce: Dielec[] }[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const createMutation = useCreateDielec(zakazkaId, zostavaId);
  const hromadne = useHromadneZostavy(zakazkaId);
  const nastavenia = useNastaveniaGlobal();
  const predvolenaRezia = nastavenia.data?.data.vlastna_rezia_pct ?? "20";

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      nazov: "",
      dodavatel: "",
      mj: "ks",
      mnozstvo: "1",
      nakup: "",
      rezia: String(Number(predvolenaRezia)),
      praca: "",
    },
  });
  const { register, handleSubmit, control, setValue, getFieldState, formState } = form;

  // Nastavenia prídu neskôr než prvé vykreslenie — réžiu doplnia, kým ju
  // user sám neprepísal (okno sa pripája pri každom otvorení nanovo)
  useEffect(() => {
    if (!getFieldState("rezia").isDirty) {
      setValue("rezia", String(Number(predvolenaRezia)));
    }
  }, [predvolenaRezia, getFieldState, setValue]);

  const [nakup, rezia, mnozstvo, mj, praca] = useWatch({
    control,
    name: ["nakup", "rezia", "mnozstvo", "mj", "praca"],
  });
  const predaj = predajVlastnej(parseDecimal(nakup), parseDecimal(rezia), parseDecimal(praca));
  const mnozstvoCislo = Number(parseDecimal(mnozstvo)) || 0;

  const ciele = [{ id: zostavaId, kod: zostavaKod, existingDielce }, ...dalsieZostavy];
  const viacZostav = ciele.length > 1;
  const bezi = createMutation.isPending || formState.isSubmitting;

  async function onSubmit(v: FormData): Promise<void> {
    const riadok = (kod: string, poradie: number): DielecCreate => ({
      kod,
      poradie,
      nazov_strojovy: v.nazov,
      // Ks je pri vlastnej položke vždy 1 — množstvo nesie vlastna_mnozstvo
      pocet_ks: 1,
      material_slot_id: null,
      material_id_direct: null,
      operacia: null,
      x_mm: null,
      y_mm: null,
      z_mm: null,
      nadmiera_x_mm: "0.00",
      nadmiera_y_mm: "0.00",
      nadmiera_aktivna: false,
      smer_vlakien: "0",
      hrana_x1_auto: false,
      hrana_x2_auto: false,
      hrana_y1_auto: false,
      hrana_y2_auto: false,
      vlastna_nakup: parseDecimal(v.nakup),
      vlastna_rezia_pct: parseDecimal(v.rezia),
      vlastna_mj: v.mj,
      vlastna_dodavatel: v.dodavatel || null,
      vlastna_mnozstvo: parseDecimal(v.mnozstvo),
      vlastna_praca: v.praca ? parseDecimal(v.praca) : null,
    });
    try {
      if (viacZostav) {
        const vysledok = await hromadne.pridajDielce(
          ciele.map((c) => ({
            zostavaId: c.id,
            kod: c.kod,
            data: riadok(suggestNextKod(c.kod, c.existingDielce), c.existingDielce.length),
          })),
          `pridanie vlastnej položky do ${ciele.length} zostáv`,
        );
        toast(
          vysledok.chyby.length === 0
            ? {
                title: "Vlastná položka pridaná",
                description: `${v.nazov} → ${ciele.map((c) => c.kod).join(", ")}`,
              }
            : {
                variant: "destructive",
                title: `Pridaná do ${vysledok.hotovo.length}, ${vysledok.chyby.length} neprešlo`,
                description: vysledok.chyby.map((c) => `${c.kod}: ${c.sprava}`).join(" · "),
              },
        );
      } else {
        await createMutation.mutateAsync(
          riadok(suggestNextKod(zostavaKod, existingDielce), existingDielce.length),
        );
        toast({ title: "Vlastná položka pridaná", description: v.nazov });
      }
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Vlastnú položku sa nepodarilo pridať",
        description: extractErrorMessage(err),
      });
    }
  }

  const chyba = (pole: keyof FormData): JSX.Element | null => {
    const sprava = formState.errors[pole]?.message;
    return sprava ? <p className="text-xs text-destructive">{sprava}</p> : null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Vlastná položka{" "}
            {viacZostav ? `do ${ciele.length} zostáv` : `do zostavy ${zostavaKod}`}
          </DialogTitle>
          <DialogDescription>
            Mimo cenníka — LED, oceľová konštrukcia a pod. Cenu dáš sám, do cenníka
            nič nepribudne. Zisk zostavy sa pripočíta ako pri každom riadku.
          </DialogDescription>
        </DialogHeader>
        <form
          id="vlastna-polozka"
          className="grid grid-cols-2 gap-3"
          onSubmit={(e) => void handleSubmit(onSubmit)(e)}
        >
          <div className="col-span-2 space-y-1">
            <Label htmlFor="vp-nazov">Názov *</Label>
            <Input id="vp-nazov" autoFocus placeholder="LED pás pod policou" {...register("nazov")} />
            {chyba("nazov")}
          </div>
          <div className="col-span-2 space-y-1">
            <Label htmlFor="vp-dodavatel">Dodávateľ</Label>
            <Input
              id="vp-dodavatel"
              placeholder="Elektro Novák"
              {...register("dodavatel")}
            />
            <p className="text-xs text-muted-foreground">
              V súpise pre objednávky a nákladov bude u tohto dodávateľa.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="vp-mnozstvo">Množstvo *</Label>
            <Input id="vp-mnozstvo" inputMode="decimal" placeholder="12,5" {...register("mnozstvo")} />
            {chyba("mnozstvo")}
          </div>
          <div className="space-y-1">
            <Label htmlFor="vp-mj">MJ</Label>
            <Select value={mj} onValueChange={(h) => setValue("mj", h as MjVlastnej)}>
              <SelectTrigger id="vp-mj" aria-label="Merná jednotka">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MJ_VLASTNEJ.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="vp-nakup">Nákupná cena / MJ (€) *</Label>
            <Input id="vp-nakup" inputMode="decimal" placeholder="150" {...register("nakup")} />
            {chyba("nakup")}
          </div>
          <div className="space-y-1">
            <Label htmlFor="vp-rezia">Réžia (%)</Label>
            <Input id="vp-rezia" inputMode="decimal" {...register("rezia")} />
            {chyba("rezia")}
          </div>
          <div className="space-y-1">
            <Label htmlFor="vp-praca">Práca / MJ (€)</Label>
            <Input id="vp-praca" inputMode="decimal" placeholder="0" {...register("praca")} />
            {chyba("praca")}
          </div>
          <p className="self-end pb-2 text-xs text-muted-foreground">
            Montáž, zapojenie — bez réžie, ako práca v cenníku.
          </p>
          <div
            className="col-span-2 rounded-md bg-muted px-3 py-2 text-sm"
            aria-live="polite"
            data-testid="vp-nahlad"
          >
            {predaj === null ? (
              <span className="text-muted-foreground">Zadaj nákupnú cenu — ukážem predaj.</span>
            ) : (
              <>
                Predaj {formatEur(predaj)} / {mj}
                {mnozstvoCislo > 0 ? <> · spolu <b>{formatEur(predaj * mnozstvoCislo)}</b></> : null}
                <span className="text-muted-foreground"> (pred ziskom zostavy)</span>
              </>
            )}
          </div>
        </form>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Zrušiť
          </Button>
          <Button type="submit" form="vlastna-polozka" disabled={bezi}>
            Pridať
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
